import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { THERAPISTS } from "@/data/therapists";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";
import TherapistCard from "@/components/TherapistCard";

const VALUES = [
  {
    n: "01",
    title: "Privacy is not a feature. It's the foundation.",
    text: "Many people in India delay therapy for years out of one fear: \"what if someone finds out?\" So we built Eunoia the other way around — no stored chats, no session histories, nothing to find.",
  },
  {
    n: "02",
    title: "Affordable shouldn't mean lesser.",
    text: "₹99 for a first session isn't cheap therapy. It's fairly priced therapy — the same qualified psychologists, the same care, at a price a 24-year-old on a first salary can actually sustain.",
  },
  {
    n: "03",
    title: "No judgment. Ever.",
    text: "First session or fiftieth, heartbreak or burnout, \"small\" problem or heavy one — you will be met with empathy. Stigma ends at our door.",
  },
];

export default function About() {
  const therapists = THERAPISTS;

  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 md:py-24 lg:px-24">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
              <span className="h-px w-10 bg-accent" /> About Eunoia
            </p>
            <h1 className="mt-6 max-w-4xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              <span className="italic text-accent">Eunoia</span> — from the Greek for
              "beautiful thinking; a well mind."
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              We started Eunoia because too many people in India wait years before talking to
              someone — held back by stigma, by cost, by the fear of being seen trying. We wanted
              to build the opposite of a clinic: a quiet, private, genuinely affordable place to
              be heard. A safe place to talk, to be heard, and to find your way back to yourself.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="hidden lg:col-span-5 lg:block">
            <BotanicalArt variant="branch" caption="Room to breathe." />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-screen-2xl px-6 py-20 md:px-12 md:py-28 lg:px-24">
        <Reveal>
          <h2 className="max-w-2xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            Three things we refuse to compromise on.
          </h2>
        </Reveal>
        <div className="mt-14 space-y-0">
          {VALUES.map((v, i) => (
            <Reveal key={v.n} delay={i * 0.06}>
              <div className="grid gap-6 border-t border-border py-10 md:grid-cols-12" data-testid={`value-${v.n}`}>
                <span className="font-serif text-5xl font-medium italic text-primary/15 md:col-span-2">{v.n}</span>
                <h3 className="font-serif text-2xl font-bold tracking-tight md:col-span-4 md:text-3xl">{v.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground md:col-span-6 md:text-base">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-surface">
        <div className="mx-auto max-w-screen-2xl px-6 py-20 md:px-12 md:py-28 lg:px-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-2xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
                The people behind the promise.
              </h2>
              <Link
                to="/find"
                data-testid="about-cta-find"
                className="group flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Meet your match <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {therapists.map((t, i) => (
              <TherapistCard key={t.id} therapist={t} index={i} />
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Every Eunoia therapist holds a Master's degree in psychology or clinical psychology
              and is verified before their first session with a client. We choose for warmth as
              carefully as we choose for credentials.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
